import { apiService } from '../../../../../components/api/apiService';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/identity-providers';

export interface EntraGraphAccessResult {
  users: { ok: boolean; message: string };
  groups: { ok: boolean; message: string };
  groupMemberships: { ok: boolean; message: string };
}

export interface EntraTestConnectionResult {
  status: 'success' | 'auth_failed' | 'permissions_missing';
  organization?: string | null;
  microsoftTenantId?: string | null;
  applicationClientId?: string | null;
  authentication?: string;
  connectedAt?: string | null;
  configuredBy?: string | null;
  graphAccess: EntraGraphAccessResult;
  message?: string | null;
  connectionId?: string | null;
}

function errorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  const code = axiosErr.code || '';
  const raw = axiosErr.message || '';
  if (code === 'ERR_NETWORK' || raw === 'Network Error' || /ECONNREFUSED/i.test(raw)) {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Connection test failed'
  );
}

export interface EntraSyncConfig {
  connectionId: string;
  syncEnabled: boolean;
  syncUsers: boolean;
  syncGroups: boolean;
  syncGroupMemberships: boolean;
  userScope: 'allUsers' | 'selectedGroups';
  selectedGroupIds: string[];
  attributes: {
    firstName: boolean;
    lastName: boolean;
    email: boolean;
    department: boolean;
    jobTitle: boolean;
    manager: boolean;
    employeeId: boolean;
    officeLocation: boolean;
    accountStatus: boolean;
  };
  autoCreateUsers: boolean;
  defaultPortalAccessStatus: 'none' | 'active';
  existingUserMatchMode: 'linkByEmail' | 'manualReview';
  disabledUserAction: 'updateStatusOnly' | 'suspendNonAdmins';
  deletedUserAction: 'markDeleted' | 'suspendNonAdmins';
}

export interface EntraDiscoveredGroup {
  id: string;
  displayName: string;
  description: string;
  memberCount: number;
}

export interface EntraSyncPreview {
  usersDiscovered: number;
  existingUsersMatched: number;
  newUsers: number;
  groupsDiscovered: number;
  groupMembershipsDiscovered: number;
  disabledUsers: number;
  note?: string | null;
}

export async function getEntraSyncConfig(connectionId: string): Promise<EntraSyncConfig> {
  try {
    return await apiService.get(`${BASE}/entra/${connectionId}/sync-config`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function saveEntraSyncConfig(connectionId: string, payload: EntraSyncConfig): Promise<EntraSyncConfig> {
  try {
    return await apiService.put(`${BASE}/entra/${connectionId}/sync-config`, payload, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function listEntraGroups(connectionId: string, search?: string): Promise<EntraDiscoveredGroup[]> {
  try {
    return await apiService.get(
      `${BASE}/entra/${connectionId}/groups`,
      search ? { search } : {},
      CONNECTION
    );
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function previewEntraSync(connectionId: string): Promise<EntraSyncPreview> {
  try {
    return await apiService.post(`${BASE}/entra/${connectionId}/sync/preview`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export interface EntraSsoConfig {
  connectionId: string;
  isEnabled: boolean;
  protocol: 'oidc';
  enforcementMode: 'optional' | 'requiredForDirectoryUsers' | 'requiredForTenant';
  allowEmailLinking: boolean;
  redirectUri: string;
  lastTestAt?: string | null;
  lastTestStatus?: string | null;
}

export interface EntraReview {
  connection: {
    organization?: string | null;
    status: string;
    graphAccess: string;
    microsoftTenantId?: string | null;
  };
  sync: EntraSyncConfig;
  preview: EntraSyncPreview;
  sso: EntraSsoConfig;
}

export interface EntraFinishResult {
  connectionId: string;
  setupCompleted: boolean;
  usersCreated: number;
  usersUpdated: number;
  rolesCreatedOrUpdated: number;
  message: string;
}

function portalApiBase(): string {
  const configured = process.env.REACT_APP_API_URL_backend;
  if (configured) return configured.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'production') return 'https://portalapi.selficai.com';
  return 'http://localhost:5032';
}

export function entraSsoLoginUrl(connectionId?: string, mode: 'test' | 'login' = 'test'): string {
  const base = portalApiBase();
  const params = new URLSearchParams({ mode });
  if (connectionId) params.set('connectionId', connectionId);
  return `${base}/api/auth/entra/login?${params.toString()}`;
}

export async function getEntraSsoConfig(connectionId: string): Promise<EntraSsoConfig> {
  try {
    return await apiService.get(`${BASE}/entra/${connectionId}/sso-config`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function saveEntraSsoConfig(
  connectionId: string,
  payload: Pick<EntraSsoConfig, 'isEnabled' | 'enforcementMode' | 'allowEmailLinking'>
): Promise<EntraSsoConfig> {
  try {
    return await apiService.put(`${BASE}/entra/${connectionId}/sso-config`, payload, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function getEntraReview(connectionId: string): Promise<EntraReview> {
  try {
    return await apiService.get(`${BASE}/entra/${connectionId}/review`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function finishEntraSetup(connectionId: string): Promise<EntraFinishResult> {
  try {
    return await apiService.post(`${BASE}/entra/${connectionId}/finish`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function syncEntraDirectory(connectionId: string): Promise<EntraFinishResult> {
  try {
    return await apiService.post(`${BASE}/entra/${connectionId}/sync`, {}, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export interface IdentityProviderListItem {
  id: string;
  kind: 'entra';
  name: string;
  status: 'connected' | 'not-connected' | 'syncing' | 'error';
  organization?: string | null;
  microsoftTenantId?: string | null;
  clientId?: string | null;
  connectionId: string;
  userCount: number;
  groupCount: number;
  autoSync: boolean;
  lastSyncAt?: string | null;
  ssoEnabled: boolean;
  setupCompleted: boolean;
  configuredBy?: string | null;
  features: Array<'Directory' | 'SSO' | 'Users' | 'Groups' | 'Provisioning'>;
}

export interface EntraDisconnectResult {
  deletedUsers: number;
  deletedRoles: number;
  message: string;
}

export async function disconnectEntraDirectory(connectionId: string): Promise<EntraDisconnectResult> {
  try {
    return await apiService.delete(`${BASE}/entra/${connectionId}`, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function listIdentityProviders(): Promise<IdentityProviderListItem[]> {
  try {
    const response = await apiService.get(`${BASE}`, {}, CONNECTION);
    return Array.isArray(response) ? response : [];
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

export async function testEntraConnection(payload: {
  tenantId: string;
  clientId: string;
  clientSecret: string;
  secretExpiration?: string;
}): Promise<EntraTestConnectionResult> {
  try {
    return await apiService.post(`${BASE}/entra/test-connection`, payload, CONNECTION);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}
